import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Stack,
  Divider,
} from '@mui/material'
import {
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { authService, MfaStatus } from '../../services/authService'
import { MfaSetupDialog } from './MfaSetupDialog'
import { MfaDisableDialog } from './MfaDisableDialog'
import { BackupCodesDisplay } from './BackupCodesDisplay'

export const MfaSetupSection: React.FC = () => {
  const { t } = useTranslation('profile')
  const [mfaStatus, setMfaStatus] = useState<MfaStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [setupDialogOpen, setSetupDialogOpen] = useState(false)
  const [disableDialogOpen, setDisableDialogOpen] = useState(false)
  const [backupCodesDialogOpen, setBackupCodesDialogOpen] = useState(false)
  const [backupCodes, setBackupCodes] = useState<string[]>([])

  const fetchMfaStatus = async () => {
    try {
      setLoading(true)
      setError(null)
      const status = await authService.getMfaStatus()
      setMfaStatus(status)
    } catch (err: any) {
      setError(err.response?.data?.error || 'MFA状態の取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMfaStatus()
  }, [])

  const handleSetupComplete = (codes: string[]) => {
    setBackupCodes(codes)
    setSetupDialogOpen(false)
    setBackupCodesDialogOpen(true)
    fetchMfaStatus()
  }

  const handleDisableComplete = () => {
    setDisableDialogOpen(false)
    fetchMfaStatus()
  }

  const handleRegenerateComplete = (codes: string[]) => {
    setBackupCodes(codes)
    setBackupCodesDialogOpen(true)
    fetchMfaStatus()
  }

  if (loading) {
    return (
      <Paper sx={{ p: 3, height: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      </Paper>
    )
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, height: '100%' }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    )
  }

  return (
    <>
      <Paper sx={{ p: 3, height: '100%' }}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon color="primary" />
            <Typography variant="h6">{t('mfa.title', '2段階認証')}</Typography>
          </Box>
          <Divider />

          <Typography variant="body2" color="text.secondary">
            {t('mfa.description', '認証アプリを使用して、アカウントのセキュリティを強化します')}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1">{t('mfa.statusLabel', 'ステータス')}:</Typography>
            {mfaStatus?.enabled ? (
              <Chip
                icon={<CheckCircleIcon />}
                label={t('mfa.status.enabled', '有効')}
                color="primary"
                size="small"
              />
            ) : (
              <Chip label={t('mfa.status.disabled', '無効')} color="default" size="small" />
            )}
          </Box>

          {mfaStatus?.enabled && (
            <>
              {mfaStatus.remainingBackupCodes <= 3 && (
                <Alert severity="warning" icon={<WarningIcon />}>
                  {t(
                    'mfa.backupCodesWarning',
                    'バックアップコードが残り{{count}}個です。新しいコードを生成してください。',
                    { count: mfaStatus.remainingBackupCodes }
                  )}
                </Alert>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('mfa.remainingBackupCodes', 'バックアップコード残数')}:{' '}
                  {mfaStatus.remainingBackupCodes}
                </Typography>
              </Box>
            </>
          )}

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {mfaStatus?.enabled ? (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setBackupCodesDialogOpen(true)}
                >
                  {t('mfa.regenerateBackupCodes', 'バックアップコード再生成')}
                </Button>
                <Button variant="outlined" color="error" onClick={() => setDisableDialogOpen(true)}>
                  {t('mfa.disable', '2段階認証を無効化')}
                </Button>
              </>
            ) : (
              <Button variant="contained" color="primary" onClick={() => setSetupDialogOpen(true)}>
                {t('mfa.enable', '2段階認証を有効化')}
              </Button>
            )}
          </Box>
        </Stack>
      </Paper>

      {/* セットアップダイアログ */}
      <MfaSetupDialog
        open={setupDialogOpen}
        onClose={() => setSetupDialogOpen(false)}
        onComplete={handleSetupComplete}
      />

      {/* 無効化ダイアログ */}
      <MfaDisableDialog
        open={disableDialogOpen}
        onClose={() => setDisableDialogOpen(false)}
        onComplete={handleDisableComplete}
      />

      {/* バックアップコード表示/再生成ダイアログ */}
      <BackupCodesDisplay
        open={backupCodesDialogOpen}
        onClose={() => setBackupCodesDialogOpen(false)}
        backupCodes={backupCodes}
        showRegenerate={mfaStatus?.enabled || false}
        onRegenerate={handleRegenerateComplete}
      />
    </>
  )
}
